import React, { useState, useEffect, useRef } from "react";

// supabase import removed — admin panel uses server-side API only, never direct DB access

const PURPLE = "#6B3FA0";
const ORANGE = "#F5820A";
const GREEN = "#16a34a";
const RED = "#dc2626";
const BLUE = "#1E5FA8";
const AMBER = "#d97706";

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000; // 15 minutes
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export default function AdminPanel() {
  const [authed, setAuthed] = useState(false);
  const [adminToken, setAdminToken] = useState("");
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [tab, setTab] = useState("companies");
  const [companies, setCompanies] = useState([]);
  const [licenseKeys, setLicenseKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingCompany, setEditingCompany] = useState(null);
  const [editPlan, setEditPlan] = useState("");
  const [editMaxUsers, setEditMaxUsers] = useState("");
  const [editTrialDate, setEditTrialDate] = useState("");
  const [editNoExpiry, setEditNoExpiry] = useState(false);
  const [newKeyPlan, setNewKeyPlan] = useState("starter");
  const [newKeyBusiness, setNewKeyBusiness] = useState("");
  const [newKeyEmail, setNewKeyEmail] = useState("");
  const [newKeyMsg, setNewKeyMsg] = useState("");

  // ── BRUTE FORCE PROTECTION ──
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [lockCountdown, setLockCountdown] = useState(0);

  // ── SESSION TIMEOUT ──
  const sessionTimer = useRef(null);

  useEffect(() => {
    if (!authed) return;
    const resetTimer = () => {
      clearTimeout(sessionTimer.current);
      sessionTimer.current = setTimeout(() => {
        handleLogout();
        alert("Admin session expired after 30 minutes of inactivity.");
      }, SESSION_TIMEOUT_MS);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      clearTimeout(sessionTimer.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [authed]);

  // ── LOCKOUT COUNTDOWN ──
  useEffect(() => {
    if (!lockedUntil) return;
    const iv = setInterval(() => {
      const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockedUntil(null);
        setAttempts(0);
        setLockCountdown(0);
        clearInterval(iv);
      } else {
        setLockCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [lockedUntil]);

  async function handleLogin() {
    if (lockedUntil && Date.now() < lockedUntil) return;
    setPwError("");
    try {
      var res = await fetch("/api/admin-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      var data = await res.json();
      if (data.success) {
        setAuthed(true);
        setAdminToken(data.token);
        setAttempts(0);
        loadDataWithToken(data.token);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        const remaining = MAX_ATTEMPTS - newAttempts;
        if (newAttempts >= MAX_ATTEMPTS) {
          const until = Date.now() + LOCKOUT_MS;
          setLockedUntil(until);
          setPwError("Too many failed attempts. Locked for 15 minutes.");
        } else {
          setPwError(`Incorrect password. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`);
        }
        setPw("");
      }
    } catch (e) {
      setPwError("Error connecting to server. Please try again.");
    }
  }

  function handleLogout() {
    setAuthed(false);
    setPw("");
    setPwError("");
    setAdminToken("");
    setCompanies([]);
    setLicenseKeys([]);
    clearTimeout(sessionTimer.current);
  }

  async function loadData() {
    await loadDataWithToken(adminToken);
  }

  async function callAdminApi(action, payload) {
    var res = await fetch("/api/admin-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, adminToken, payload }),
    });
    var data = await res.json();
    if (!res.ok) throw new Error(data.error || "Request failed");
    return data;
  }

  async function loadDataWithToken(token) {
    setLoading(true);
    setError("");
    try {
      var res1 = await fetch("/api/admin-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "load_companies", adminToken: token }),
      });
      var d1 = await res1.json();
      if (!res1.ok) throw new Error(d1.error || "Failed to load companies");
      setCompanies(d1.companies || []);

      var res2 = await fetch("/api/admin-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "load_license_keys", adminToken: token }),
      });
      var d2 = await res2.json();
      if (!res2.ok) throw new Error(d2.error || "Failed to load license keys");
      setLicenseKeys(d2.license_keys || []);
    } catch (e) {
      setError("Error loading data: " + e.message);
    }
    setLoading(false);
  }

  async function toggleCompany(id, currentStatus) {
    try {
      var data = await callAdminApi("toggle_company", { id, currentStatus });
      setCompanies((prev) => prev.map((c) => c.id === id ? { ...c, status: data.newStatus } : c));
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  async function updateCompany(id) {
    try {
      var trialExpiry = null;
      if (!editNoExpiry) {
        if (!editTrialDate) {
          alert("Pick a trial end date or tick No expiry.");
          return;
        }
        // End of the chosen day in the admin's local time (PST/PDT), stored as UTC
        trialExpiry = new Date(editTrialDate + "T23:59:59").toISOString();
      }
      var data = await callAdminApi("update_company", { id, plan: editPlan, maxUsers: editMaxUsers, trialExpiry });
      setCompanies((prev) => prev.map((c) => c.id === id ? { ...c, ...data.updates } : c));
      // Session 32: confirm what was actually saved
      var u = data.updates || {};
      alert("Saved: " + String(u.plan || "").toUpperCase() +
        (u.trial_expires_at ? ", trial ends " + new Date(u.trial_expires_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : ", full access (no expiry)") +
        (u.status ? ", status " + u.status : ""));
      setEditingCompany(null);
      setEditPlan("");
      setEditMaxUsers("");
      setEditTrialDate("");
      setEditNoExpiry(false);
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  async function generateNewKey() {
    setNewKeyMsg("");
    try {
      var data = await callAdminApi("generate_key", { plan: newKeyPlan, businessName: newKeyBusiness, ownerEmail: newKeyEmail });
      setNewKeyMsg("✅ Created: " + data.key);
      setNewKeyBusiness("");
      setNewKeyEmail("");
      var res2 = await fetch("/api/admin-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "load_license_keys", adminToken }),
      });
      var d2 = await res2.json();
      setLicenseKeys(d2.license_keys || []);
    } catch (e) {
      setNewKeyMsg("Error: " + e.message);
    }
  }

  async function toggleKey(id, currentStatus) {
    try {
      var data = await callAdminApi("toggle_key", { id, currentStatus });
      setLicenseKeys((prev) => prev.map((k) => k.id === id ? { ...k, status: data.newStatus } : k));
    } catch (e) {
      alert("Error: " + e.message);
    }
  }

  function trialDaysLeft(trial_expires_at) {
    if (!trial_expires_at) return null;
    const diff = new Date(trial_expires_at) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  // ── STATS ──
  const totalCompanies = companies.length;
  const activeCompanies = companies.filter(c => c.status === "active").length;
  const trialCompanies = companies.filter(c => c.trial).length;
  const paidCompanies = companies.filter(c => !c.trial && c.status === "active").length;

  // ── LOGIN SCREEN ──
  if (!authed) {
    const isLocked = lockedUntil && Date.now() < lockedUntil;
    const attemptsLeft = MAX_ATTEMPTS - attempts;
    return (
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1a1035 0%, #2d1b5e 50%, #1a1035 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, fontFamily: "Arial, sans-serif",
      }}>
        <div style={{
          background: "white", borderRadius: 20, padding: 32,
          maxWidth: 400, width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}>
          {/* Logo */}
          <div style={{
            width: 70, height: 74, borderRadius: 16,
            background: PURPLE, border: "3px solid #1a1a1a",
            display: "flex", flexDirection: "column",
            overflow: "hidden", margin: "0 auto 16px",
          }}>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: "800", color: "white" }}>B</div>
            <div style={{ background: "#C46000", padding: "4px 0 8px", textAlign: "center", fontSize: 11, fontWeight: "800", color: "white", letterSpacing: 1 }}>PRO</div>
          </div>

          <h1 style={{ textAlign: "center", margin: "0 0 4px", fontSize: 24, fontWeight: "800", color: PURPLE }}>Admin Panel</h1>
          <p style={{ textAlign: "center", color: "#666", fontSize: 13, margin: "0 0 8px" }}>BlindsPro CRM — Restricted Access</p>

          {/* Attempt indicator */}
          {attempts > 0 && !isLocked && (
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16 }}>
              {Array.from({ length: MAX_ATTEMPTS }).map((_, i) => (
                <div key={i} style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: i < attempts ? RED : "#e0e0e0",
                }} />
              ))}
            </div>
          )}

          {/* Lockout banner */}
          {isLocked && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, padding: "14px 16px", marginBottom: 16, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>🔒</div>
              <div style={{ fontWeight: "bold", color: RED, fontSize: 15, marginBottom: 4 }}>Account Locked</div>
              <div style={{ color: "#666", fontSize: 13 }}>Too many failed attempts.</div>
              <div style={{ color: RED, fontWeight: "bold", fontSize: 18, marginTop: 8 }}>
                {Math.floor(lockCountdown / 60)}:{String(lockCountdown % 60).padStart(2, "0")}
              </div>
              <div style={{ color: "#666", fontSize: 12 }}>remaining</div>
            </div>
          )}

          {pwError && !isLocked && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: RED, fontSize: 13, marginBottom: 14 }}>
              ⚠️ {pwError}
            </div>
          )}

          {!isLocked && (
            <>
              <div style={{ position: "relative", marginBottom: 16 }}>
                <input
                  type={showPw ? "text" : "password"}
                  placeholder="Admin password"
                  value={pw}
                  onChange={(e) => { setPw(e.target.value); setPwError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  style={{
                    width: "100%", padding: "13px 50px 13px 14px",
                    borderRadius: 8, border: "1.5px solid #e0e0e0",
                    fontSize: 15, boxSizing: "border-box",
                    outline: "none", color: "#1a1a1a",
                  }}
                />
                <button onClick={() => setShowPw(!showPw)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: "#666" }}>
                  {showPw ? "🙈" : "👁️"}
                </button>
              </div>

              {attempts > 0 && (
                <div style={{ fontSize: 12, color: AMBER, textAlign: "center", marginBottom: 10, fontWeight: "bold" }}>
                  ⚠️ {attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} remaining before lockout
                </div>
              )}

              <button onClick={handleLogin}
                style={{ width: "100%", padding: 13, background: ORANGE, color: "white", border: "none", borderRadius: 8, fontSize: 15, fontWeight: "bold", cursor: "pointer" }}>
                Login to Admin Panel
              </button>
            </>
          )}

          <div style={{ textAlign: "center", marginTop: 20, paddingTop: 16, borderTop: "1px solid #e0e0e0" }}>
            <a href="/" style={{ fontSize: 13, color: PURPLE, fontWeight: "bold", textDecoration: "none" }}>← Back to App</a>
          </div>
        </div>
      </div>
    );
  }

  // ── ADMIN DASHBOARD ──
  const trialsExpiringSoon = companies.filter((c) => {
    const days = trialDaysLeft(c.trial_expires_at);
    return days !== null && days >= 0 && days <= 5;
  });

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 20px", fontFamily: "Arial, sans-serif", background: "#f7f8fa", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #2d1b5e, #6B3FA0)", borderRadius: 16, padding: "20px 24px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: "800", color: "white" }}>B</div>
            <div style={{ background: "#C46000", padding: "1px 0", textAlign: "center", fontSize: 8, fontWeight: "800", color: "white", letterSpacing: 1 }}>PRO</div>
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: "800", color: "white" }}>BlindsPro Admin</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>Session expires after 30 min idle</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={loadData} style={{ padding: "8px 16px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: "bold", color: "white" }}>🔄 Refresh</button>
          <button onClick={handleLogout} style={{ padding: "8px 16px", background: RED, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: "bold", color: "white" }}>🚪 Logout</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Total Companies", value: totalCompanies, color: PURPLE, icon: "🏢" },
          { label: "Active", value: activeCompanies, color: GREEN, icon: "✅" },
          { label: "On Trial", value: trialCompanies, color: AMBER, icon: "⏳" },
          { label: "Paid", value: paidCompanies, color: BLUE, icon: "💰" },
        ].map((k, i) => (
          <div key={i} style={{ background: "white", borderRadius: 12, padding: "16px 18px", border: "1px solid #e0e0e0", borderLeft: "4px solid " + k.color }}>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>{k.icon} {k.label}</div>
            <div style={{ fontSize: 28, fontWeight: "800", color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[
          { key: "companies", label: "🏢 Companies" },
          { key: "license_keys", label: "🔑 License Keys" },
          { key: "revenue", label: "💰 Revenue" },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: "10px 20px", borderRadius: 10, border: "none", cursor: "pointer",
              fontSize: 14, fontWeight: "bold",
              background: tab === t.key ? PURPLE : "white",
              color: tab === t.key ? "white" : "#555",
              boxShadow: tab === t.key ? "0 2px 8px rgba(107,63,160,0.3)" : "0 1px 3px rgba(0,0,0,0.08)",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: RED, fontSize: 13, marginBottom: 16 }}>❌ {error}</div>}
      {loading && <div style={{ textAlign: "center", padding: 40, color: "#666", fontSize: 15 }}>⏳ Loading...</div>}

      {/* ── COMPANIES TAB ── */}
      {tab === "companies" && !loading && (
        <>
          {trialsExpiringSoon.length > 0 && (
            <div style={{ background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: "bold", color: AMBER, fontSize: 15, marginBottom: 10 }}>⚠️ Trials Expiring Soon ({trialsExpiringSoon.length})</div>
              {trialsExpiringSoon.map((c) => (
                <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: "white", borderRadius: 8, marginBottom: 6, border: "1px solid #fde68a" }}>
                  <span style={{ fontWeight: "600" }}>{c.business_name || c.company_id}</span>
                  <span style={{ color: RED, fontWeight: "bold" }}>{trialDaysLeft(c.trial_expires_at)} days left</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #e0e0e0", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: "800", fontSize: 16, color: "#1a1a1a" }}>All Companies ({companies.length})</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f7f8fa" }}>
                    {["Company", "Plan", "Max Users", "Status", "Trial End", "Created", "Actions"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 12, fontWeight: "700", color: "#555", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "2px solid #e0e0e0" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c, idx) => {
                    const days = trialDaysLeft(c.trial_expires_at);
                    const planColors = { business: PURPLE, pro: BLUE, starter: GREEN, trial: AMBER };
                    const statusColors = { active: { bg: "#dcfce7", text: GREEN }, disabled: { bg: "#fee2e2", text: RED }, trial: { bg: "#fef3c7", text: AMBER }, past_due: { bg: "#fef3c7", text: AMBER }, cancelled: { bg: "#fee2e2", text: RED }, inactive: { bg: "#fee2e2", text: RED } };
                    const sc = statusColors[c.status] || statusColors.trial;
                    return (
                      <tr key={c.id} style={{ background: idx % 2 === 0 ? "white" : "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontWeight: "700", color: "#1a1a1a", fontSize: 14 }}>{c.business_name || c.company_id}</div>
                          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{c.owner_email}</div>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          {editingCompany === c.id ? (
                            <select value={editPlan} onChange={(e) => setEditPlan(e.target.value)}
                              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #e0e0e0", fontSize: 13 }}>
                              <option value="starter">Starter</option>
                              <option value="pro">Pro</option>
                              <option value="business">Business</option>
                            </select>
                          ) : (
                            <span style={{ background: planColors[c.plan] || "#999", color: "white", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "700" }}>
                              {(c.plan || "trial").toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          {editingCompany === c.id ? (
                            <span style={{ fontWeight: "600", color: "#333" }}>{({ starter: 1, pro: 5, business: 10 })[editPlan] || "—"} <span style={{ fontSize: 11, color: "#888", fontWeight: "normal" }}>(from plan)</span></span>
                          ) : (
                            <span style={{ fontWeight: "600", color: "#333" }}>{c.max_users || "—"}</span>
                          )}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{ background: sc.bg, color: sc.text, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "700" }}>
                            {(c.status || "trial").toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: days !== null && days <= 3 ? RED : "#333", fontWeight: days !== null && days <= 3 ? "bold" : "normal" }}>
                          {editingCompany === c.id ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              <input type="date" value={editTrialDate} disabled={editNoExpiry}
                                onChange={(e) => setEditTrialDate(e.target.value)}
                                style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #e0e0e0", fontSize: 13, color: "#333", fontWeight: "normal", opacity: editNoExpiry ? 0.5 : 1 }} />
                              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555", fontWeight: "normal", cursor: "pointer" }}>
                                <input type="checkbox" checked={editNoExpiry} onChange={(e) => setEditNoExpiry(e.target.checked)} />
                                No expiry (full access, not a trial)
                              </label>
                            </div>
                          ) : c.trial_expires_at ? (
                            <>
                              <div>{days !== null && days >= 0 ? days + "d left" : "Expired"}</div>
                              <div style={{ fontSize: 11, color: "#888", fontWeight: "normal", marginTop: 2 }}>{new Date(c.trial_expires_at).toLocaleDateString("en-CA")}</div>
                            </>
                          ) : "—"}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#666" }}>
                          {c.created_at ? new Date(c.created_at).toLocaleDateString("en-CA") : "—"}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {editingCompany === c.id ? (
                              <>
                                <button onClick={() => updateCompany(c.id)}
                                  style={{ padding: "5px 12px", background: GREEN, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: "bold" }}>Save</button>
                                <button onClick={() => { setEditingCompany(null); setEditPlan(""); setEditMaxUsers(""); setEditTrialDate(""); setEditNoExpiry(false); }}
                                  style={{ padding: "5px 12px", background: "#999", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: "bold" }}>Cancel</button>
                              </>
                            ) : (
                              <button onClick={() => { setEditingCompany(c.id); setEditPlan(c.plan || "pro"); setEditMaxUsers(c.max_users || ""); setEditTrialDate(c.trial_expires_at ? new Date(c.trial_expires_at).toLocaleDateString("en-CA") : ""); setEditNoExpiry(c.trial === false && !c.trial_expires_at); /* Session 32 */ }}
                                style={{ padding: "5px 12px", background: BLUE, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: "bold" }}>✏️ Edit</button>
                            )}
                            <button onClick={() => toggleCompany(c.id, c.status)}
                              style={{ padding: "5px 12px", background: c.status === "disabled" ? GREEN : RED, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: "bold" }}>
                              {c.status === "disabled" ? "✅ Enable" : "🚫 Disable"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── LICENSE KEYS TAB ── */}
      {tab === "license_keys" && !loading && (
        <>
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #e0e0e0", padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: "800", fontSize: 16, color: "#1a1a1a", marginBottom: 16 }}>🔑 Generate New License Key</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: "bold" }}>Plan</div>
                <select value={newKeyPlan} onChange={(e) => setNewKeyPlan(e.target.value)}
                  style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e0e0e0", fontSize: 14, outline: "none" }}>
                  <option value="starter">Starter — 1 user ($19/mo)</option>
                  <option value="pro">Pro — 5 users ($39/mo)</option>
                  <option value="business">Business — 10 users ($59/mo)</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: "bold" }}>Business Name (optional)</div>
                <input style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e0e0e0", fontSize: 14, outline: "none", minWidth: 200 }}
                  placeholder="e.g. Smith Blinds Co." value={newKeyBusiness} onChange={(e) => setNewKeyBusiness(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: "bold" }}>Owner Email (optional)</div>
                <input style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e0e0e0", fontSize: 14, outline: "none", minWidth: 220 }}
                  placeholder="owner@company.com" value={newKeyEmail} onChange={(e) => setNewKeyEmail(e.target.value)} />
              </div>
              <button onClick={generateNewKey}
                style={{ padding: "10px 24px", background: PURPLE, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: "bold" }}>
                ✨ Generate Key
              </button>
            </div>
            {newKeyMsg && (
              <div style={{
                marginTop: 14, padding: "12px 16px", borderRadius: 8, fontSize: 14, fontWeight: "600",
                background: newKeyMsg.startsWith("Error") ? "#fef2f2" : "#f0fdf4",
                border: "1px solid " + (newKeyMsg.startsWith("Error") ? "#fca5a5" : "#86efac"),
                color: newKeyMsg.startsWith("Error") ? RED : GREEN,
              }}>
                {newKeyMsg}
              </div>
            )}
          </div>

          <div style={{ background: "white", borderRadius: 12, border: "1px solid #e0e0e0", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #e0e0e0" }}>
              <div style={{ fontWeight: "800", fontSize: 16, color: "#1a1a1a" }}>All License Keys ({licenseKeys.length})</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f7f8fa" }}>
                    {["Key", "Business", "Plan", "Status", "Activated", "Actions"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: 12, fontWeight: "700", color: "#555", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "2px solid #e0e0e0" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {licenseKeys.map((k, idx) => {
                    const statusColors = { active: { bg: "#dcfce7", text: GREEN }, used: { bg: "#fef3c7", text: AMBER }, suspended: { bg: "#fee2e2", text: RED } };
                    const sc = statusColors[k.status] || statusColors.active;
                    const planColors = { business: PURPLE, pro: BLUE, starter: GREEN };
                    return (
                      <tr key={k.id} style={{ background: idx % 2 === 0 ? "white" : "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{ fontFamily: "monospace", fontWeight: "700", fontSize: 13, background: "#f0f0f0", padding: "3px 8px", borderRadius: 6 }}>{k.key}</span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ fontSize: 13, fontWeight: "600" }}>{k.business_name || "—"}</div>
                          <div style={{ fontSize: 11, color: "#666" }}>{k.owner_email || ""}</div>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{ background: planColors[k.plan] || "#999", color: "white", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "700" }}>
                            {(k.plan || "").toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{ background: sc.bg, color: sc.text, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "700" }}>
                            {(k.status || "").toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#666" }}>
                          {k.used_at ? new Date(k.used_at).toLocaleDateString("en-CA") : "Not yet"}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <button onClick={() => toggleKey(k.id, k.status)}
                            style={{ padding: "5px 12px", background: k.status === "suspended" ? GREEN : RED, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: "bold" }}>
                            {k.status === "suspended" ? "✅ Activate" : "🚫 Suspend"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── REVENUE TAB ── */}
      {tab === "revenue" && !loading && (
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e0e0e0", padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
          <div style={{ fontSize: 18, fontWeight: "800", color: "#1a1a1a", marginBottom: 8 }}>Revenue Dashboard</div>
          <div style={{ fontSize: 14, color: "#666" }}>Stripe integration coming soon. Check your Stripe dashboard at dashboard.stripe.com</div>
          <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer"
            style={{ display: "inline-block", marginTop: 16, padding: "10px 24px", background: PURPLE, color: "white", borderRadius: 8, textDecoration: "none", fontSize: 14, fontWeight: "bold" }}>
            Open Stripe Dashboard →
          </a>
        </div>
      )}
    </div>
  );
}
